import { style, globalStyle } from '@vanilla-extract/css';
import { vars } from '../../theme/contract.css';
import { mobile, reducedMotion } from '../../theme/media';
import { glassRimLight } from '../../styles/glass.css';

export const container = style({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: 1,
  minHeight: 0,
});

// ============================================
// TOOLBAR (inside editorWrapper)
// ============================================

export const toolbar = style({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  // 본문 .tiptap과 같은 16px 레일. 36px 버튼은 글리프가 박스 중앙(+10px)이라
  // padding 6px로 당겨 글리프 좌측이 16px에 앉게 한다(광학 정렬).
  padding: '8px 6px',
  borderBottom: `1px solid ${vars.surface.liquidGlass.innerSurface.border}`,
  background: vars.opacityScale.blackOverlay.light,
});

// 서식 버튼 줄. 폭과 무관하게 한 줄로 두고 넘치면 가로로 민다. 줄바꿈을 두면 폰에서 하단 띠가
// 90px 이 됐고(2026-10-01), 데스크톱 640px 메모 창에서도 끝 자리가 붙자 두 줄로 꺾였다(2026-10-02).
export const toolbarScroll = style({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  flexWrap: 'nowrap',
  overflowX: 'auto',
  scrollbarWidth: 'none',
  flex: 1,
  minWidth: 0,
  // 버튼 ::after 히트 영역(±4px)이 스크롤 상자에 잘리지 않게 위아래로 늘였다가 마진으로 되돌린다.
  padding: '4px 0',
  margin: '-4px 0',
  '@media': {
    [mobile]: {
      paddingRight: '24px',
      // 오른쪽 끝을 흐려 버튼이 더 있음을 알린다.
      maskImage: 'linear-gradient(to right, black calc(100% - 24px), transparent)',
      WebkitMaskImage: 'linear-gradient(to right, black calc(100% - 24px), transparent)',
    },
  },
});

globalStyle(`${toolbarScroll}::-webkit-scrollbar`, { display: 'none' });

// 서식 줄이 밀려도 제자리에 남는 끝 자리 — 도움말(?)과 호출부 슬롯(예: 파일 추가).
export const toolbarEnd = style({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  flexShrink: 0,
});

// toolbarPlacement 'bottom' — 본문 아래 띠라 경계선이 위로 간다.
export const toolbarBottom = style({
  borderBottom: 'none',
  borderTop: `1px solid ${vars.surface.liquidGlass.innerSurface.border}`,
});

export const toolbarGroup = style({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
});

export const compactToolbar = style({});
globalStyle(`${compactToolbar} ${toolbarScroll}`, {
  paddingRight: 0,
  maskImage: 'none',
  WebkitMaskImage: 'none',
});
export const slashButton = style({
  width: 'auto',
  padding: '0 8px',
  whiteSpace: 'nowrap',
  fontFamily: vars.typography.fontFamily.primary,
});

// Polish: border.secondary는 글래스 위에서 거의 invisible — primary 톤으로 강화 + spacing 확대.
// MEDIUM-8 audit이 false-positive였지만 시각 강도 ↑ polish는 가치 있음.
export const toolbarDivider = style({
  width: '1px',
  height: '20px',
  background: vars.surface.border.primary,
  margin: '0 4px',
  flexShrink: 0,
});

export const toolbarButton = style({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  fontSize: vars.typography.fontSize.sm,
  fontFamily: vars.typography.fontFamily.mono,
  fontWeight: vars.typography.fontWeight.bold,
  color: vars.typography.textColor.tertiary,
  background: 'none',
  border: '1px solid transparent',
  borderRadius: '4px',
  cursor: 'pointer',
  transition: `color ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, background ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, border-color ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,
  flexShrink: 0,
  selectors: {
    [`${slashButton}&`]: {
      width: 'auto',
      minWidth: '36px',
    },
    '&:hover': {
      color: vars.typography.textColor.secondary,
      background: vars.surface.highlight.light,
      borderColor: vars.surface.border.secondary,
    },
  },
  '@media': {
    [reducedMotion]: { transition: 'none' },
  },
});

// Touch-target hit area expansion (WCAG 2.5.5 — 44x44 minimum)
// Visual is 36x36, hit area extends 4px on each side via invisible ::after overlay
globalStyle(`${toolbarButton}::after`, {
  content: '""',
  position: 'absolute',
  top: '-4px',
  right: '-4px',
  bottom: '-4px',
  left: '-4px',
});

export const toolbarButtonItalic = style({
  fontStyle: 'italic',
});

export const toolbarButtonStrikethrough = style({
  textDecoration: 'line-through',
});

export const toolbarButtonSm = style({
  fontSize: vars.typography.fontSize['2xs'],
});

export const toolbarButtonXs = style({
  fontSize: vars.typography.fontSize['3xs'],
});

export const toolbarButtonActive = style({
  color: vars.bright.primary.light,
  background: vars.surface.highlight.medium,
  borderColor: vars.surface.highlight.strong,
});

// ============================================
// TIPTAP EDITOR
// ============================================

export const editorWrapper = style({
  borderRadius: '8px',
  border: `1px solid ${vars.surface.liquidGlass.innerSurface.border}`,
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: vars.surface.liquidGlass.innerSurface.background,
  backgroundImage: `radial-gradient(ellipse at 50% 40%, ${vars.opacityScale.whiteOverlay.light} 0%, transparent 40%, ${vars.opacityScale.blackOverlay.fade} 100%)`,
  backdropFilter: vars.surface.liquidGlass.backdropFilter.light,
  boxShadow: `inset 0 0 20px ${vars.opacityScale.blackOverlay.medium}, inset 0 1px 0 ${vars.opacityScale.whiteOverlay.veil}, inset 0 -2px 6px ${vars.opacityScale.blackOverlay.strong}, 0 0 6px ${vars.bright.primary.glow}`,
  transition: `border-color ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  selectors: {
    '&:focus-within': {
      borderColor: vars.surface.highlight.strong,
      boxShadow: `0 0 0 2px ${vars.surface.highlight.light}`,
    },
  },
  '@media': {
    [reducedMotion]: { transition: 'none' },
  },
});

globalStyle(`${editorWrapper}::after`, glassRimLight);

export const dropOverlay = style({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `${vars.opacityScale.primaryBlue.subtleLight}`,
  border: `2px dashed ${vars.bright.primary.default}`,
  borderRadius: '8px',
  color: vars.bright.primary.light,
  fontSize: vars.typography.fontSize.sm,
  fontWeight: vars.typography.fontWeight.bold,
  fontFamily: vars.typography.fontFamily.mono,
  zIndex: vars.elevation.interactive,
  pointerEvents: 'none',
});

export const tiptapEditor = style({
  minHeight: '140px',
  outline: 'none',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

// Inner ProseMirror contenteditable — default density 'compact' (14px)
globalStyle(`${tiptapEditor} .tiptap`, {
  padding: '12px 16px',
  fontFamily: vars.typography.fontFamily.primary,
  fontSize: vars.typography.fontSize.sm,
  lineHeight: vars.typography.lineHeight.relaxed,
  color: vars.typography.textColor.primary,
  outline: 'none',
  minHeight: '140px',
  flex: 1,
});

// PR5: density 'comfortable' modifier (eng D2)
// 16px body + line-height 1.6 + padding +. iOS Safari 자동 줌 차단(≥16px) + 가독성.
// editorWrapper에 동일 element compound class로 추가, descendant override.
export const editorComfortable = style({});

globalStyle(`${editorComfortable} ${tiptapEditor} .tiptap`, {
  padding: '14px 16px',
  // body 토큰 (1rem = 16px) — iOS Safari 자동 줌 차단 임계 충족
  fontSize: vars.typography.fontSize.body,
  lineHeight: '1.6',
});

// 괘선 표면(ruled) — 글줄마다 hairline, 본문 왼쪽 40px 에 여백선. 글줄이 괘선에 앉으려면
// 줄 높이가 괘선 주기와 같아야 해서 p 마진을 0 으로 접는다.
// ponytail: 제목·목록·인용은 자기 마진이 있어 그 뒤부터 괘선과 어긋난다 — 블록 마진을 주기의
// 배수로 맞추면 풀리지만 메모 본문은 문단 위주라 우선 문단만 맞춘다.
// 선언 순서 주의: editorComfortable 의 .tiptap 규칙과 특이도가 같아 뒤에 와야 이긴다.
const RULE_PERIOD = 28;
export const editorRuled = style({});

globalStyle(`${editorRuled} ${tiptapEditor}`, {
  position: 'relative',
});

globalStyle(`${editorRuled} ${tiptapEditor}::before`, {
  content: '""',
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: '40px',
  width: '1px',
  background: vars.surface.border.primary,
  pointerEvents: 'none',
});

globalStyle(`${editorRuled} ${tiptapEditor} .tiptap`, {
  paddingLeft: '54px',
  lineHeight: `${RULE_PERIOD}px`,
  backgroundImage: `repeating-linear-gradient(180deg, transparent 0, transparent ${RULE_PERIOD - 1}px, ${vars.surface.highlight.medium} ${RULE_PERIOD - 1}px, ${vars.surface.highlight.medium} ${RULE_PERIOD}px)`,
  backgroundOrigin: 'content-box',
  backgroundClip: 'content-box',
});

globalStyle(`${editorRuled} ${tiptapEditor} .tiptap p`, { margin: 0 });

// Placeholder
globalStyle(`${tiptapEditor} .tiptap p.is-editor-empty:first-child::before`, {
  content: 'attr(data-placeholder)',
  float: 'left',
  color: vars.typography.textColor.tertiary,
  pointerEvents: 'none',
  height: 0,
});

// Tiptap content element styles
globalStyle(`${tiptapEditor} h1, ${tiptapEditor} h2, ${tiptapEditor} h3`, {
  fontWeight: String(vars.typography.fontWeight.bold),
  color: vars.typography.textColor.primary,
  marginTop: '16px',
  marginBottom: '8px',
});

globalStyle(`${tiptapEditor} h1`, {
  fontSize: vars.typography.fontSize.lg,
  borderBottom: `1px solid ${vars.surface.border.secondary}`,
  paddingBottom: '4px',
});

globalStyle(`${tiptapEditor} h2`, { fontSize: vars.typography.fontSize.md });
globalStyle(`${tiptapEditor} h3`, { fontSize: vars.typography.fontSize.base });

globalStyle(`${tiptapEditor} p`, { margin: '0 0 8px' });

globalStyle(`${tiptapEditor} ul, ${tiptapEditor} ol`, {
  paddingLeft: '20px',
  margin: '0 0 8px',
});

globalStyle(`${tiptapEditor} li`, { marginBottom: '2px' });

// Task list (checkbox list)
globalStyle(`${tiptapEditor} ul[data-type="taskList"]`, {
  listStyle: 'none',
  paddingLeft: '4px',
});

globalStyle(`${tiptapEditor} ul[data-type="taskList"] li`, {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
});

globalStyle(`${tiptapEditor} ul[data-type="taskList"] li label`, {
  flexShrink: 0,
  marginTop: '3px',
});

globalStyle(`${tiptapEditor} code`, {
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: '0.9em',
  padding: '2px 6px',
  borderRadius: '4px',
  background: vars.surface.highlight.light,
  color: vars.bright.primary.light,
});

globalStyle(`${tiptapEditor} pre`, {
  padding: '12px',
  borderRadius: '6px',
  background: vars.surface.background.tertiary,
  border: `1px solid ${vars.surface.border.secondary}`,
  overflow: 'auto',
  margin: '0 0 8px',
});

globalStyle(`${tiptapEditor} pre code`, {
  padding: 0,
  background: 'none',
  color: vars.typography.textColor.primary,
});

globalStyle(`${tiptapEditor} blockquote`, {
  borderLeft: `3px solid ${vars.bright.primary.default}`,
  paddingLeft: '12px',
  margin: '0 0 8px',
  color: vars.typography.textColor.secondary,
  fontStyle: 'italic',
});

globalStyle(`${tiptapEditor} a`, {
  color: vars.bright.primary.light,
  textDecoration: 'underline',
  cursor: 'pointer',
});

globalStyle(`${tiptapEditor} strong`, {
  fontWeight: String(vars.typography.fontWeight.bold),
  color: vars.typography.textColor.primary,
});

globalStyle(`${tiptapEditor} em`, {
  fontStyle: 'italic',
});

globalStyle(`${tiptapEditor} s`, {
  textDecoration: 'line-through',
  color: vars.typography.textColor.tertiary,
});

globalStyle(`${tiptapEditor} hr`, {
  border: 'none',
  borderTop: `1px solid ${vars.surface.border.secondary}`,
  margin: '12px 0',
});

globalStyle(`${tiptapEditor} table`, {
  width: '100%',
  borderCollapse: 'collapse',
  margin: '0 0 8px',
  fontSize: vars.typography.fontSize.sm,
});

globalStyle(`${tiptapEditor} th, ${tiptapEditor} td`, {
  padding: '6px 10px',
  border: `1px solid ${vars.surface.border.secondary}`,
  textAlign: 'left',
  minWidth: '60px',
});

globalStyle(`${tiptapEditor} th`, {
  background: vars.surface.background.tertiary,
  fontWeight: String(vars.typography.fontWeight.semibold),
});

// Selected table cell
globalStyle(`${tiptapEditor} .selectedCell`, {
  background: vars.surface.highlight.light,
});

globalStyle(`${tiptapEditor} .tiptap > :first-child`, { marginTop: '0' });
globalStyle(`${tiptapEditor} .tiptap > :last-child`, { marginBottom: '0' });

// ============================================
// HELP PANEL
// ============================================

export const helpButton = style({
  position: 'relative',
  width: '36px',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: vars.typography.fontSize.xs,
  fontFamily: vars.typography.fontFamily.mono,
  fontWeight: vars.typography.fontWeight.bold,
  color: vars.typography.textColor.tertiary,
  background: 'none',
  border: `1px solid ${vars.surface.border.secondary}`,
  borderRadius: '50%',
  cursor: 'pointer',
  transition: `color ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, border-color ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,
  flexShrink: 0,
  selectors: {
    // 끝 자리에 혼자 남으면 테두리 있는 박스라 박스 우측을 16px 레일에 맞춘다(padding 6 + 10).
    '&:last-child': { marginRight: '10px' },
    '&:hover': {
      color: vars.bright.primary.light,
      borderColor: vars.bright.primary.default,
    },
  },
  '@media': {
    [reducedMotion]: { transition: 'none' },
  },
});

// Touch-target hit area expansion (WCAG 2.5.5 — 44x44 minimum)
globalStyle(`${helpButton}::after`, {
  content: '""',
  position: 'absolute',
  top: '-4px',
  right: '-4px',
  bottom: '-4px',
  left: '-4px',
});

export const helpButtonActive = style({
  color: vars.bright.primary.light,
  borderColor: vars.bright.primary.default,
  background: vars.surface.highlight.light,
});

export const helpPanel = style({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: '4px 16px',
  padding: '10px 14px',
  background: vars.opacityScale.blackOverlay.light,
  border: `1px solid ${vars.surface.liquidGlass.innerSurface.border}`,
  borderRadius: '6px',
  fontSize: vars.typography.fontSize['2xs'],
});

export const helpRow = style({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '2px 0',
});

export const helpHeading = style({
  gridColumn: '1 / -1',
  margin: '4px 0',
  color: vars.typography.textColor.primary,
  fontSize: vars.typography.fontSize.xs,
});

export const helpSyntax = style({
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize['2xs'],
  color: vars.bright.primary.light,
  background: vars.surface.highlight.light,
  padding: '1px 6px',
  borderRadius: '3px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
});

export const helpDesc = style({
  color: vars.typography.textColor.tertiary,
});

// ============================================
// READ-ONLY MARKDOWN BODY (for HistoryView)
// ============================================

export const markdownBody = style({
  fontFamily: vars.typography.fontFamily.primary,
  fontSize: vars.typography.fontSize.sm,
  lineHeight: vars.typography.lineHeight.relaxed,
  color: vars.typography.textColor.primary,
  wordBreak: 'break-word',
});

export const previewEmpty = style({
  color: vars.typography.textColor.tertiary,
  fontStyle: 'italic',
  fontSize: vars.typography.fontSize.sm,
});

// Read-only markdown body element styles
globalStyle(`${markdownBody} h1, ${markdownBody} h2, ${markdownBody} h3`, {
  fontWeight: String(vars.typography.fontWeight.bold),
  color: vars.typography.textColor.primary,
  marginTop: '16px',
  marginBottom: '8px',
});

globalStyle(`${markdownBody} h1`, {
  fontSize: vars.typography.fontSize.lg,
  borderBottom: `1px solid ${vars.surface.border.secondary}`,
  paddingBottom: '4px',
});

globalStyle(`${markdownBody} h2`, { fontSize: vars.typography.fontSize.md });
globalStyle(`${markdownBody} h3`, { fontSize: vars.typography.fontSize.base });
globalStyle(`${markdownBody} p`, { margin: '0 0 8px' });

globalStyle(`${markdownBody} ul, ${markdownBody} ol`, {
  paddingLeft: '20px',
  margin: '0 0 8px',
});

globalStyle(`${markdownBody} li`, { marginBottom: '2px' });

globalStyle(`${markdownBody} code`, {
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: '0.9em',
  padding: '2px 6px',
  borderRadius: '4px',
  background: vars.surface.highlight.light,
  color: vars.bright.primary.light,
});

globalStyle(`${markdownBody} pre`, {
  padding: '12px',
  borderRadius: '6px',
  background: vars.surface.background.secondary,
  border: `1px solid ${vars.surface.border.secondary}`,
  overflow: 'auto',
  margin: '0 0 8px',
});

globalStyle(`${markdownBody} pre code`, { padding: 0, background: 'none' });

globalStyle(`${markdownBody} blockquote`, {
  borderLeft: `3px solid ${vars.bright.primary.default}`,
  paddingLeft: '12px',
  margin: '0 0 8px',
  color: vars.typography.textColor.secondary,
  fontStyle: 'italic',
});

globalStyle(`${markdownBody} a`, {
  color: vars.bright.primary.light,
  textDecoration: 'underline',
});

globalStyle(`${markdownBody} strong`, {
  fontWeight: String(vars.typography.fontWeight.bold),
  color: vars.typography.textColor.primary,
});

globalStyle(`${markdownBody} em`, {
  fontStyle: 'italic',
});

globalStyle(`${markdownBody} table`, {
  width: '100%',
  borderCollapse: 'collapse',
  margin: '0 0 8px',
  fontSize: vars.typography.fontSize.sm,
});

globalStyle(`${markdownBody} th, ${markdownBody} td`, {
  padding: '6px 10px',
  border: `1px solid ${vars.surface.border.secondary}`,
  textAlign: 'left',
});

globalStyle(`${markdownBody} th`, {
  background: vars.surface.background.tertiary,
  fontWeight: String(vars.typography.fontWeight.semibold),
});

globalStyle(`${markdownBody} hr`, {
  border: 'none',
  borderTop: `1px solid ${vars.surface.border.secondary}`,
  margin: '12px 0',
});

globalStyle(`${markdownBody} > :first-child`, { marginTop: '0' });
globalStyle(`${markdownBody} > :last-child`, { marginBottom: '0' });

// ============================================
// 위키 모드 — 편집기 안의 [[문서 링크]] · [^각주] 강조 (wikiSyntax.ts 의 장식 클래스)
// 글자는 그대로 두고 링크 모양만 입힌다. 전경은 bright.primary.light, 면은 highlight 단.
// ============================================
globalStyle(`${tiptapEditor} .wiki-syntax-link`, {
  color: vars.bright.primary.light,
  background: vars.surface.highlight.light,
  borderRadius: '3px',
  padding: '0 2px',
});

globalStyle(`${tiptapEditor} .wiki-syntax-footnote`, {
  color: vars.bright.primary.light,
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize.xs,
  verticalAlign: 'super',
});

// ============================================
// 위키 그림 — 편집기 안 그림은 칸 너비를 넘지 않는다. 올리는 중·실패는 본문 아래 한 줄.
// ============================================

globalStyle(`${tiptapEditor} .tiptap img`, {
  display: 'block',
  maxWidth: 'min(100%, 560px)',
  height: 'auto',
  margin: '8px 0',
  borderRadius: '4px',
});

export const toolbarHint = style({
  fontSize: vars.typography.fontSize['2xs'],
  color: vars.typography.textColor.tertiary,
  whiteSpace: 'nowrap',
  paddingInline: '4px',
});

export const imageUploadStatus = style({
  padding: '6px 16px',
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.secondary,
});

export const imageUploadError = style({
  color: vars.bright.status.error,
});
